import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';

/**
 * Popover que aparece ao passar mouse sobre trabalho no calendário
 * Mostra informações do PDF extraídas pela IA
 */
const JobHoverPopover = ({ job, extractedData, position, onClose }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Mostrar imediatamente
    setIsVisible(true);
  }, []);

  // Detectar se é mobile
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;

  if (!isVisible) return null;

  // Se carregando dados
  if (extractedData._loading) {
    return (
      <div 
        data-popover="true"
        className="fixed z-[99999] bg-white border-2 border-blue-400 rounded-lg shadow-2xl p-2"
        style={{
          left: `${position.x}px`,
          top: `${position.y}px`,
          animation: 'fadeIn 0.15s ease-in',
          width: isMobile ? '280px' : 'auto',
          minWidth: isMobile ? '280px' : '300px'
        }}
      >
        <div className="flex items-center gap-2 text-xs text-blue-700">
          <div className="animate-spin">⏳</div>
          <div>Carregando...</div>
        </div>
      </div>
    );
  }

  // Se IA ainda está processando
  if (extractedData._processing) {
    return (
      <div 
        data-popover="true"
        className="fixed z-[99999] bg-white border-2 border-yellow-400 rounded-lg shadow-2xl p-3"
        style={{
          left: `${position.x}px`,
          top: `${position.y}px`,
          animation: 'fadeIn 0.15s ease-in',
          width: isMobile ? '280px' : 'auto'
        }}
      >
        <div className="flex items-center gap-2 text-sm text-yellow-700">
          <div className="animate-spin">🤖</div>
          <div>IA processando PDF...</div>
        </div>
        <div className="text-xs text-gray-500 mt-1">
          Aguarde ~15 segundos
        </div>
      </div>
    );
  }

  // SERVICE: balão verde mostrando o Scope of Work das notas
  if (extractedData._service) {
    const notes = extractedData.service_notes || '';
    const idx = notes.toLowerCase().indexOf('scope of work');
    const scopeLines = [];
    if (idx >= 0) {
      const lines = notes.slice(idx).split('\n');
      for (let i = 0; i < lines.length; i++) {
        if (i > 0 && !lines[i].trim()) break;
        scopeLines.push(lines[i]);
      }
    }
    return (
      <div
        data-popover="true"
        data-testid="service-scope-popover"
        className="fixed z-[99999] bg-white border-2 border-emerald-400 rounded-lg shadow-2xl"
        style={{
          left: `${position.x}px`,
          top: `${position.y}px`,
          animation: 'fadeIn 0.15s ease-in',
          maxHeight: '80vh',
          overflowY: 'auto',
          width: isMobile ? '280px' : '340px',
          minWidth: isMobile ? '280px' : '340px',
          maxWidth: isMobile ? '280px' : '340px'
        }}
      >
        <div className="bg-emerald-50 p-2 rounded-t-lg border-b border-emerald-200 flex items-start justify-between">
          <div className="flex-1">
            <div className="font-semibold text-emerald-900 text-xs leading-tight">
              <span className="bg-emerald-600 text-white rounded px-1 mr-1">S</span>
              {extractedData.customer_name} - #{extractedData.order_number || 'N/A'}
            </div>
            {(extractedData.address || extractedData.city) && (
              <div className="text-[10px] text-gray-600 mt-0.5 leading-tight">
                📍 {extractedData.address}{extractedData.city ? `, ${extractedData.city}` : ''}
              </div>
            )}
            <div className="text-[10px] text-gray-600 mt-0.5 leading-tight">
              🪟 {extractedData.windows || 0} janelas • 🚪 {extractedData.doors || 0} portas
            </div>
          </div>
          <button
            onClick={onClose}
            className="ml-2 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
        <div className="p-2">
          {scopeLines.length > 0 ? (
            <div className="text-[10px] text-gray-700 space-y-0.5">
              <div className="font-bold text-emerald-800 leading-tight">{scopeLines[0]}</div>
              {scopeLines.slice(1).map((l, i) => (
                <div key={i} className="leading-tight">{l}</div>
              ))}
            </div>
          ) : (
            <div className="text-[10px] text-gray-700 whitespace-pre-wrap leading-tight">
              {notes ? notes.slice(0, 600) : 'Sem notas de serviço'}
            </div>
          )}
        </div>
      </div>
    );
  }

  const { windows = [], doors = [], job_notes = '', customer_name = '', address = '', city = '', exterior_finish = '' } = extractedData;

  // Determinar tipo de acabamento exterior
  const getFinishType = () => {
    // 1. Usar exterior_finish da IA (extraído da Materials Checklist)
    if (exterior_finish && exterior_finish !== 'Unknown') return exterior_finish;
    
    // 2. Fallback: verificar job_notes
    const notes = (job_notes || '').toLowerCase();
    if (notes.includes('cap') || notes.includes('coil')) return 'Exterior Cap';
    if (notes.includes('l trim') || notes.includes('ltrim')) return 'Exterior LTrim';
    
    return null;
  };

  // Contar ocorrências de cada tipo de serviço no misc
  const countServiceOccurrences = () => {
    const services = {};
    const patterns = [
      { key: 'Replace Brickmold', patterns: ['replace.*brickmold', 'replace.*brick mold'] },
      { key: 'Replace Sill', patterns: ['replace.*sill'] },
      { key: 'Replace Nosing', patterns: ['replace.*nosing'] },
      { key: 'Cut back Vinyl', patterns: ['cut.*back.*vinyl', 'cut.*vinyl', 'vinyl.*cut'] },
      { key: 'J-Channel', patterns: ['j-channel', 'j channel', 'jchannel'] },
      { key: 'Replace Trim Interior', patterns: ['replace.*trim.*interior', 'replace.*interior.*trim'] }
    ];

    [...windows, ...doors].forEach(item => {
      const misc = (item.misc || '').toLowerCase();
      
      patterns.forEach(({ key, patterns: pats }) => {
        if (pats.some(pat => new RegExp(pat, 'i').test(misc))) {
          services[key] = (services[key] || 0) + 1;
        }
      });
    });

    return services;
  };

  const finishType = getFinishType();
  const services = countServiceOccurrences();

  return (
    <div 
      data-popover="true"
      className="fixed z-[99999] bg-white border-2 border-blue-400 rounded-lg shadow-2xl"
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
        animation: 'fadeIn 0.15s ease-in',
        maxHeight: '80vh',
        overflowY: 'auto',
        width: isMobile ? '280px' : '340px',
        minWidth: isMobile ? '280px' : '340px',
        maxWidth: isMobile ? '280px' : '340px'
      }}
    >
      {/* Header Minimalista */}
      <div className="bg-blue-50 p-2 rounded-t-lg border-b border-blue-200 flex items-start justify-between">
        <div className="flex-1">
          <div className="font-semibold text-blue-900 text-xs leading-tight">
            {customer_name || job.customer_name} - #{job.order_number || 'N/A'}
          </div>
          <div className="text-[10px] text-gray-600 mt-0.5 leading-tight">
            📍 {address || `${job.address}, ${job.city || ''}`}
          </div>
          <div className="text-[10px] text-gray-600 mt-0.5 leading-tight">
            🪟 {windows.length} janelas • 🚪 {doors.length} portas
          </div>
        </div>
        <button
          onClick={onClose}
          className="ml-2 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X className="h-3 w-3" />
        </button>
      </div>

      {/* Body */}
      <div className="p-2 space-y-1">

        {/* Lista de Janelas e Portas - SEM scroll interno, cresce com conteúdo */}
        <div className="space-y-0.5 text-[10px] text-gray-700 border-b border-gray-200 pb-1">
          {windows.map((w, i) => {
            // Determinar label do tipo
            const type = (w.type || '').toLowerCase();
            let label = 'PW'; // Picture por padrão
            if (type.includes('glider') || type.includes('gliding')) label = 'GL';
            else if (type.includes('double-hung') || type.includes('double hung')) label = 'DH';
            else if (type.includes('awning')) label = 'AW';
            else if (type.includes('casement')) label = 'CS';
            else if (type.includes('hopper')) label = 'HP';
            else if (type.includes('bay')) label = 'BAY';
            else if (type.includes('bow')) label = 'BOW';
            else if (type.includes('specialty') || type.includes('circle') || type.includes('half spring') || type.includes('springline')) label = 'SPW';
            
            return (
              <div key={`w-${i}`} className="font-mono leading-tight flex justify-between items-center gap-1">
                <span>
                  <span className="font-bold">{w.number}</span> - <span className="font-bold">{w.room}</span>
                </span>
                <span className="font-bold text-right whitespace-nowrap">
                  <span className="text-blue-600 mr-1">{label}</span>{w.width} x {w.height}
                </span>
              </div>
            );
          })}
          {doors.map((d, i) => {
            // Extrair tipo simples (Gliding, Inswing, etc.)
            const simpleType = d.type.match(/(Gliding|Inswing|Outswing|Single|Double|Patio)/i)?.[0] || 'Door';
            // Mostrar largura x altura se disponível
            const dimensions = d.height ? `${d.width}" x ${d.height}"` : `${d.width}"`;
            return (
              <div key={`d-${i}`} className="font-mono text-orange-700 leading-tight flex justify-between items-center gap-1">
                <span>
                  🚪 <span className="font-bold">{d.number} ({simpleType})</span> - <span className="font-bold">{d.room}</span>
                </span>
                <span className="font-bold text-right whitespace-nowrap">{dimensions}</span>
              </div>
            );
          })}
        </div>

        {/* Notas Importantes - sem scroll */}
        {(finishType || Object.keys(services).length > 0) && (
          <div className="space-y-0.5 text-[10px] pt-1">
            {finishType && (
              <div className="text-blue-700 leading-tight font-semibold">• {finishType}</div>
            )}
            {Object.entries(services).map(([service, count]) => (
              <div key={service} className="text-gray-700 leading-tight">
                • {service} em {count}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default JobHoverPopover;
